import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop'
import { CommonModule } from '@angular/common'
import { AfterViewInit, ChangeDetectorRef, Component, DestroyRef, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core'
import { FormsModule, ReactiveFormsModule, UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms'
import { MatDialog } from '@angular/material/dialog'
import { MatTooltipModule } from '@angular/material/tooltip'
import { TagsComponent } from '@app/modules/@micetribe/events/tags/components/tags/tags.component'
import { LegalSetup } from '@app/modules/events/types/legal-setup.type'
import { DialogComponent } from '@app/modules/shared/components/dialog/dialog.component'
import { AuthService } from '@auth/services/auth.service'
import { environment } from '@environments/environment'
import { NbFormFieldModule, NbInputModule, NbSelectModule, NbToggleModule } from '@nebular/theme'
import { BillingService } from '@payments/services/billing.service'
import { PromoCode } from '@personas/interfaces/promo-code.interface'
import { HandshakesFactoryService } from '@personas/models/handshakes.model'
import { Observable, of, Subject } from 'rxjs'
import { first } from 'rxjs/operators'
import { NumericOnlyDirective } from '../../directives/numeric-only/numeric-only.directive'
import { ConfirmationsRemindersComponent } from '../confirmations-reminders/confirmations-reminders.component'
import { FormElementsComponent } from '../form-elements/form-elements.component'

import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { Checkin } from '@app/v2/event/types/event.type'
/**
 * Represents a persona details
 */
@Component({
  selector: 'ngx-persona-form',
  templateUrl: './form.component.html',
  styleUrls: ['./form.component.scss'],
  standalone: true,
  imports: [
    NbInputModule,
    CommonModule,
    NbFormFieldModule,
    NbToggleModule,
    FormsModule,
    ReactiveFormsModule,
    TagsComponent,
    FormElementsComponent,
    ConfirmationsRemindersComponent,
    NbSelectModule,
    NumericOnlyDirective,
    MatTooltipModule
  ]
})
export class FormComponent implements OnInit, OnChanges, AfterViewInit {
  /** Subject that stops all hot observables */
  private _destroyRef = inject(DestroyRef)

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['promoCodes']) {
      this.initializePromoCodes()
    }
  }

  /**
   * Persona field flagging
   * @deprecated
   */
  personaFieldFlag = false
  /**
   * Persona fields controls
   */
  personaFields: UntypedFormControl[]

  /**
   * Persona model
   */
  @Input()
  form: any

  /**
   * Persona name control
   */
  @Input()
  name: UntypedFormControl

  /**
   * Persona passcode control
   */
  @Input()
  pass_code: UntypedFormControl

  /**
   * Whether the persona is protected with a passcode
   */
  isProtected = false

  /**
   * Requires approval control
   */
  @Input()
  require_approval: UntypedFormControl

  /**
   * Maximum registrations control
   */
  @Input()
  maximum_registrations: UntypedFormControl

  /**
   * Success message control
   */
  @Input()
  message: UntypedFormControl

  /**
   * Regret message control
   */
  @Input()
  regret: UntypedFormControl

  /**
   * Persona fields form array
   */
  @Input()
  fields: UntypedFormArray

  /**
   * List of all personas
   */
  @Input()
  personas: any

  /**
   * Event model
   */
  @Input()
  event: any

  @Input()
  index: number
  /**
   * Currency id control
   */
  @Input()
  currency_id: UntypedFormControl

  /**
   * Registration fees amount control
   */
  @Input()
  registration_amount: UntypedFormControl

  @Input()
  personasRegstrationCount: { form: string; count: number }[]

  /**
   * Is the persona collecting payment
   */
  @Input()
  enable_payment: UntypedFormControl

  /**
   * List of promo codes for the event
   */
  @Input()
  promoCodes: PromoCode[]
  @Input() eventLegalSetup: LegalSetup

  promoCodes$: Observable<PromoCode[]>
  /**
   * Is the component ready
   */
  ready = false
  /**
   * Current elements types
   */
  elementsType: Subject<number[]> = new Subject()
  /**
   * Current elements names
   */
  elementsName: Subject<string[]> = new Subject()
  /**
   * Is the workspace connected to stripe
   */
  isStripeConnected = false
  /**
   * Is the workspace a main entity
   */
  isMainEntity = false

  /**
   * List of available currencies for selection
   */
  currencyList = []

  hasProducts: boolean

  /**
   * @Event emit when persona name change
   */

  @Output()
  nameChanged: EventEmitter<boolean> = new EventEmitter()
  /**
   * Save Parsona Method
   */
  @Output()
  savePersona: EventEmitter<any> = new EventEmitter()

  constructor(
    private hsf: HandshakesFactoryService,
    private paymentService: BillingService,
    private auth: AuthService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef
  ) {}

  /**
   * Initializes the component
   */
  ngOnInit() {
    // Initialize isProtected based on pass_code value
    this.isProtected = !!this.pass_code?.value

    this.maximum_registrations.valueChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((value) =>
      this.maximum_registrations.setValue(value ? parseInt(value.toString(), 10) : null, {
        emitEvent: false
      })
    )
    this.isStripeConnected = this.auth.currentWorkspace.can_collect_payment
    this.isMainEntity = this.auth.role === 'Main Entity' || this.auth.role === 'Admin'
    this.paymentService
      .getCurrencyList(this.auth.currentWorkspace.id)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((res: any) => {
        this.currencyList = res
      })
    if (!this.currency_id.value) this.currency_id.patchValue(1)
    this.hasProducts = !!this.form.value.products_count

    this.initializePromoCodes()
  }

  /**
   * Initialize promoCodes$ observable
   */
  private initializePromoCodes(): void {
    if (this.promoCodes && Array.isArray(this.promoCodes)) {
      const selectedPromoCodes = this.form.get('promo_codes')?.value || []
      this.promoCodes.forEach((code) => {
        const selectedPromoCode = selectedPromoCodes.find((x) => x.id === code.id)
        code['active'] = !!selectedPromoCode
        if (!!selectedPromoCode) selectedPromoCode['active'] = true
      })

      this.form.get('promo_codes')?.patchValue(selectedPromoCodes, { emitEvent: false })

      this.promoCodes$ = of(this.promoCodes)
    } else {
      this.promoCodes$ = of([])
    }
  }

  /**
   * Sets the component ready stat 2 seconds after rendering
   */
  ngAfterViewInit() {
    // Use Promise.resolve() to defer execution to the next microtask, avoiding ExpressionChangedAfterItHasBeenCheckedError
    Promise.resolve().then(() => {
      if (!this.hasPaymentGateway()) {
        this.form.controls['enable_payment'].setValue(false, { emitEvent: false })
        this.form.controls['registration_amount'].setValue(null, { emitEvent: false })
      }

      this.ready = true
      this.cdr.detectChanges()
    })
  }

  /**
   * Updates a component property with a value - form control
   * @param property string
   * @param value string
   */
  update(property, value) {
    const paymentValue = { amount: this.form.controls['registration_amount'].value, isPaid: this.form.controls['enable_payment'].value }
    this[property].patchValue(value, { emitEvent: false })
    if (property === 'enable_payment' && value) {
      const formElements = this.form.get('form_elements').value
      formElements.forEach((element) => {
        if (['fullname', 'email', 'phone'].includes(element.name)) {
          element.is_required = 3
          element.is_disable_online = 0
          element.is_disable_onsite = 0
        }
      })
      this.form.get('form_elements').patchValue(formElements)
      this.form.get('form_elements').updateValueAndValidity()
    }
    if (property === 'enable_payment' && value === false && this.personasRegstrationCount.find((o) => o.form === this.form.value.slug).count > 0) {
      const dialogData = {
        headerText: `Cannot refund registrants after switching to free persona`,
        cancelButtonText: 'Cancel',
        confirmButtonText: 'Confirm',
        confirmButtonClass: 'delete',
        hideIcon: true
      }
      const _declineModalRef = this.dialog.open(DialogComponent, {
        minWidth: '544px',
        panelClass: 'matdialog',
        data: dialogData
      })

      _declineModalRef
        .afterClosed()
        .pipe(first(), takeUntilDestroyed(this._destroyRef))
        .subscribe((res) => {
          if (res === 'confirm') {
            this.form.controls['registration_amount'].setValue(null)
            this.form.controls['enable_payment'].setValue(false)
          } else {
            this.form.controls['registration_amount'].setValue(paymentValue.amount)
            this.form.controls['enable_payment'].setValue(paymentValue.isPaid)
          }
        })
    }

    if (property === 'name') this.nameChanged.emit(value ? true : false)
  }

  /**
   * Changes the persona field weight according to it's dropped order
   * @param event CdkDragDrop
   */
  drop(event: CdkDragDrop<UntypedFormGroup>) {
    if (this.form.controls.form_elements) {
      this.personaFields = [...this.form.controls.form_elements.controls]
      moveItemInArray(this.personaFields, event.previousIndex, event.currentIndex)
      this.resetFieldsWeight()
      this.form.controls.form_elements.controls = [...this.personaFields]
    }
  }

  /**
   * Resets the persona fields weight according to their current order
   */
  resetFieldsWeight() {
    this.form.controls.form_elements.controls.forEach((item) => {
      item.value.weight = this.form.controls.form_elements.controls.indexOf(item)
    })
  }

  /**
   * Deletes a certain persona field
   * @param index number
   */
  deleteField(index: number) {
    this.form.controls['form_elements'].removeAt(index)
    this.resetFieldsWeight()
  }

  /**
   * Gets elements names
   */
  get elementNames(): string[] {
    const formArray = this.form.controls['form_elements'] as UntypedFormArray
    return formArray.value.map((data) => data['name'])
  }
  /**
   * Whether the persona has handshake enabled
   */
  get hasHandshake(): boolean {
    return this.hsf.hasHandshake(this.form)
  }
  /**
   * Toggles certain features based on environment
   */
  get isEnabled(): boolean {
    return !environment.production
  }

  /**
   * List of available zones
   */
  private _availableZones: any[] = null
  /**
   * Shadow list of available zones
   */
  private _shadowedZones: Checkin[]
  /**
   * Sets the available zones
   */
  @Input()
  public set availableZones(value: Checkin[]) {
    if (!Array.isArray(value) || !value.length) {
      this._shadowedZones = []
      this._availableZones = []
      return
    }
    const normalizedZones = this._normalizeZonesForTags(value)
    if (this._isSameZoneList(normalizedZones, this._shadowedZones)) {
      return
    }
    this._shadowedZones = normalizedZones
    this._availableZones = this._buildAvailableZoneOptions(normalizedZones)
  }
  /**
   * Gets the available zones
   */
  public get availableZones() {
    return this._availableZones
  }
  /**
   * Updates this persona's selected zones
   * @param selectedIds number[]
   */
  updateAvailableZones(selectedIds: Array<number> = []) {
    const next = this._withEventVenue(selectedIds)
    this.form.get('sub_locations_ids').setValue(next)
    this._availableZones = this._buildAvailableZoneOptions(this._shadowedZones ?? [], next)
  }

  /**
   * Normalizes the zones for tags
   * @param value Zone[]
   * @returns Zone[]
   */
  private _normalizeZonesForTags(value: Checkin[]): Checkin[] {
    if (!Array.isArray(value) || !value.length) return []

    const rootsWithChildren = value.filter((zone: Checkin) => Array.isArray(zone?.subcheckins) && zone.subcheckins.length > 0)
    if (!rootsWithChildren.length) return value

    const flattenedChildren = rootsWithChildren.flatMap((zone: Checkin) => zone.subcheckins || [])
    return flattenedChildren.length ? flattenedChildren : value
  }

  /**
   * Builds the available zone options
   * @param zones Checkin[]
   * @param selected Array<number>
   * @returns any[]
   */
  private _buildAvailableZoneOptions(zones: Checkin[], selected: Array<number | string> = []): any[] {
    const globalId = zones.find((o) => o.slug === 'global')?.id
    const selectedIds = this._withEventVenue(selected.length ? selected : (this.form.get('sub_locations_ids').value as Array<number | string>) ?? [], zones)
    return zones.map((o) => {
      const isEventVenue = o.slug === 'global'
      return {
        id: o.id,
        name: o.name,
        active: isEventVenue || selectedIds.some((selectedId) => String(selectedId) === String(o.id)),
        indent: o.slug !== 'global' && o.slug !== 'global_virtual',
        alsoSelect: o.parent === globalId ? o.parent : null,
        disabled: isEventVenue,
        separator: o.slug === 'global_virtual'
      }
    })
  }

  /**
   * Event Venue (slug `global`) is required on every persona and must stay selected.
   * @param selectedIds Array<number | string>
   * @param zones Checkin[]
   * @returns Array<number | string>
   */
  private _withEventVenue(selectedIds: Array<number | string> = [], zones: Checkin[] = this._shadowedZones ?? []): Array<number | string> {
    const globalId = zones.find((zone) => zone.slug === 'global')?.id
    if (globalId == null || selectedIds.some((id) => String(id) === String(globalId))) {
      return selectedIds
    }
    return [...selectedIds, globalId]
  }

  /**
   * Checks if the two zone lists are the same
   * @param next Checkin[]
   * @param prev Checkin[]
   * @returns boolean
   */
  private _isSameZoneList(next: Checkin[] = [], prev: Checkin[] = []): boolean {
    if (!next?.length && !prev?.length) return true
    if (!next || !prev || next.length !== prev.length) return false
    for (let i = 0; i < next.length; i++) {
      if (String(next[i]?.id) !== String(prev[i]?.id)) return false
    }
    return true
  }
  /**
   * Update this persona's selected promo codes
   * @param event any[]
   */
  updateSelectedPromoCodes(event: number[]) {
    this.form.get('promo_code_ids').setValue(event)
    const selectedPromoCodes = this.promoCodes.filter((o) => event.includes(o.id))
    selectedPromoCodes.forEach((o) => {
      o['active'] = true
    })
    this.promoCodes
      .filter((o) => !event.includes(o.id))
      .forEach((o) => {
        o['active'] = false
      })
    this.form.get('promo_codes').setValue(selectedPromoCodes)
  }
  /**
   * Navigates to stripe connect in a new tab
   */
  navigateToOrganizerPage() {
    this.paymentService
      .getStripeLink()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((res: any) => {
        window.open(res.url, '_blank')
      })
  }
  /**
   * Call savePersona method from Edit component to save persona
   */
  personaSave(showShareable = true) {
    this.savePersona.emit({ status: showShareable, formSlug: this.form.value.slug, index: this.index })
  }

  /**
   * The slug of the selected currency Id
   */
  currencySlug() {
    return this.currencyList.find((o) => o.id === this.currency_id?.value)?.slug
  }

  /**
   * Update pass_code form control based on toggle
   */
  togglePasscode() {
    if (this.isProtected) {
      this.pass_code.setValidators([Validators.minLength(3), Validators.maxLength(8), Validators.required])
    } else {
      this.pass_code.setValidators([Validators.minLength(3), Validators.maxLength(8)])
      this.pass_code.setValue(null)
      this.pass_code.markAsUntouched()
    }
    this.pass_code.updateValueAndValidity()
    this.cdr.detectChanges() // Force change detection after updating the form control
  }

  hasPaymentGateway(): boolean {
    return this.auth.currentWorkspace.options?.payments?.gateway === undefined ? false : true
  }
}
